import type { ActionsListType } from "src/api/sure-api/actions/useGetOneAction";

const UNCATEGORIZED_CATEGORY_ID = -1;

export type TemplateCatalogLever = {
    id: number;
    title: string;
    actions: ActionsListType[];
};

export type TemplateCatalogCategory = {
    id: number;
    title: string;
    iconName?: string;
    levers: TemplateCatalogLever[];
    total: number;
    engaged: number;
};

type CategoryGroup = Omit<TemplateCatalogCategory, "levers" | "total" | "engaged"> & {
    levers: Map<number, TemplateCatalogLever>;
    actionIds: Set<number>;
};

const addActionToCategories = (
    action: ActionsListType,
    categories: Map<number, CategoryGroup>,
    uncategorizedTitle: string,
): void => {
    const themes =
        action.csrThemes.length > 0 ?
            action.csrThemes
        :   [{ id: UNCATEGORIZED_CATEGORY_ID, title: uncategorizedTitle, parent: undefined, iconName: undefined }];
    for (const theme of themes) {
        const parent = theme.parent ?? theme;
        let category = categories.get(parent.id);

        if (!category) {
            category = {
                id: parent.id,
                title: parent.title,
                iconName: parent.iconName,
                levers: new Map(),
                actionIds: new Set(),
            };
            categories.set(parent.id, category);
        }

        let lever = category.levers.get(theme.id);

        if (!lever) {
            lever = { id: theme.id, title: theme.title, actions: [] };
            category.levers.set(theme.id, lever);
        }

        if (!lever.actions.some((item) => item.id === action.id)) {
            lever.actions.push(action);
        }

        category.actionIds.add(action.id);
    }
};

const groupActionsByCategory = (actions: ActionsListType[], uncategorizedTitle: string): CategoryGroup[] => {
    const categories = new Map<number, CategoryGroup>();

    for (const action of actions) {
        addActionToCategories(action, categories, uncategorizedTitle);
    }

    return [...categories.values()];
};

export const groupTemplateCatalog = (
    actions: ActionsListType[],
    addedIds: number[],
    uncategorizedTitle: string,
): TemplateCatalogCategory[] => {
    const categories = groupActionsByCategory(actions, uncategorizedTitle);
    const added = new Set(addedIds);

    return categories.map((category) => {
        const { actionIds, levers, ...details } = category;

        return {
            ...details,
            levers: [...levers.values()],
            total: actionIds.size,
            engaged: [...actionIds].filter((id) => added.has(id)).length,
        };
    });
};
